import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useOnboarding = (userId: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: onboardingData, isLoading } = useQuery({
    queryKey: ['onboarding', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await (supabase as any)
        .from('user_onboarding')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error && error.code !== 'PGRST116') throw error;
      
      // Create if doesn't exist
      if (!data) {
        const { data: newData, error: insertError } = await (supabase as any)
          .from('user_onboarding')
          .insert({ user_id: userId })
          .select()
          .single();
        
        if (insertError) throw insertError;
        return newData;
      }
      
      return data;
    },
    enabled: !!userId,
  });

  const updateTourProgress = useMutation({
    mutationFn: async (step: number) => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ tour_step: step })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const completeTour = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ 
          has_completed_tour: true,
          tour_step: 0
        })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      toast.success('Tour concluído! 🎉');
    },
  });

  const markChecklistItemComplete = useMutation({
    mutationFn: async (itemId: string) => {
      if (!userId) throw new Error('User not found');
      
      const currentProgress = onboardingData?.checklist_progress || {};
      const newProgress = { ...currentProgress, [itemId]: true };
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ checklist_progress: newProgress })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const markWelcomeModalSeen = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ welcome_modal_seen: true })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const skipOnboarding = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ 
          has_completed_tour: true,
          welcome_modal_seen: true
        })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      toast.info('Você pode refazer o tour nas Configurações');
    },
  });

  const savePersonalization = useMutation({
    mutationFn: async (data: {
      businessType: string;
      salesRange: string;
      primaryGoal: string;
      serviceType?: string;
      serviceDetails?: Record<string, any>;
      selectedChannels?: string[];
    }) => {
      if (!userId) throw new Error('User not found');
      
      const updateData: any = {
        business_type: data.businessType,
        daily_sales_range: data.salesRange,
        primary_goal: data.primaryGoal,
      };

      if (data.serviceType) {
        updateData.selected_service_type = data.serviceType;
      }
      if (data.serviceDetails) {
        updateData.service_details = data.serviceDetails;
      }
      if (data.selectedChannels) {
        updateData.selected_channels = data.selectedChannels;
      }
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update(updateData)
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const awardBadge = useMutation({
    mutationFn: async (badgeId: string) => {
      if (!userId) throw new Error('User not found');
      
      const currentBadges = (onboardingData?.earned_badges as string[]) || [];
      if (currentBadges.includes(badgeId)) return; // Already has this badge
      
      const newBadges = [...currentBadges, badgeId];
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ earned_badges: newBadges })
        .eq('user_id', userId);
      
      if (error) throw error;
      
      return badgeId;
    },
    onSuccess: (badgeId) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const completeOnboarding = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ onboarding_completed: true })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      toast.success('Configuração inicial concluída! 🎉');
    },
  });

  const incrementLoginCount = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const currentCount = (onboardingData?.login_count as number) || 0;
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ login_count: currentCount + 1 })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  const resetTour = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ has_completed_tour: false, tour_step: 0 })
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });

  // Trigger retention offer (sets timestamp when offer begins)
  const triggerRetentionOffer = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error('User not found');
      
      // Only trigger if not already triggered
      const { error } = await (supabase as any)
        .from('user_onboarding')
        .update({ 
          retention_offer_triggered_at: new Date().toISOString() 
        })
        .eq('user_id', userId)
        .is('retention_offer_triggered_at', null);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      queryClient.invalidateQueries({ queryKey: ['retention-offer'] });
    },
  });

  return {
    onboardingData,
    isLoading,
    updateTourProgress,
    completeTour,
    markChecklistItemComplete,
    markWelcomeModalSeen,
    skipOnboarding,
    savePersonalization,
    awardBadge,
    completeOnboarding,
    incrementLoginCount,
    resetTour,
    triggerRetentionOffer,
  };
};
